import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Cell } from 'react-table';
import RouterNavigationTabs from '@/components/organisms/RouterNavigationTabs';
import { Title } from '@/components/atoms/Title';
import { Text } from '@/components/atoms/Text';
import { Table } from '@/components/atoms/Table';
import { HeaderLeft, HeaderRight, Subtitle } from '@/components/atoms/Section/Section.style';
import { Button } from '@/components/atoms/Button';
import Modal from '@/components/atoms/Modal';
import { TableCell, TableRow } from '@/components/atoms/Table/Table.type';
import { useRestaurantTabRoutes } from '../Restaurant/hooks/useRestaurantTabRoutes';
import { ChecklistContainer, Container, Header, HeaderContainer } from './MenuOperationList.style';
import { useMenuOperationListVM } from './MenuOperationList.viewmodel';
import type { MenuOperationModuleMarketplace } from '../../services/innovorder/menu/menu.type';

const MenuOperationListPage: React.FunctionComponent<React.PropsWithChildren> = () => {
    const { isLoading, rows, headers, fetchData, refreshData, fetchDataTotalCount, downloadError, clearDownloadError } =
        useMenuOperationListVM();

    const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
    const closeErrorModal = () => setIsErrorModalOpen(false);

    const handleRowClick = React.useCallback((cell: Cell<TableRow, TableCell> | undefined) => {
        if (!cell) {
            return;
        }

        const nameCell = cell.row.original.name;
        const cellValue = nameCell?.value as {
            text: string;
            status?: string;
            moduleMarketplaceId?: string | null;
        };
        const status = cellValue?.status;
        const moduleMarketplaceId = cellValue?.moduleMarketplaceId;

        if (status === 'ERROR' && moduleMarketplaceId !== null) {
            setIsErrorModalOpen(true);
        }
    }, []);

    const tabRoutes = useRestaurantTabRoutes();

    return (
        <>
            <RouterNavigationTabs routes={tabRoutes}>
                <Container>
                    <HeaderContainer>
                        <Header>
                            <HeaderLeft>
                                <Title text="menuoperation.page.title" />
                                <Subtitle>
                                    <Text text="menuoperation.page.description" />
                                </Subtitle>
                            </HeaderLeft>
                            <HeaderRight>
                                <Button onClick={refreshData} buttonType="secondary" disabled={isLoading}>
                                    <FormattedMessage id="button.refresh" />
                                </Button>
                            </HeaderRight>
                        </Header>
                        <Table
                            rows={rows}
                            emptyMessageId="table.noRowsAfterFilter"
                            columns={headers}
                            hasPagination
                            hasUrlNavigation
                            isSortable={false}
                            fetchData={fetchData}
                            isLoading={isLoading}
                            fetchDataTotalCount={fetchDataTotalCount}
                            onRowClick={handleRowClick}
                        />
                        <Modal
                            isOpen={isErrorModalOpen}
                            onHide={closeErrorModal}
                            title="menuoperation.errorModal.title"
                            width={800}
                        >
                            <div>
                                <Text text="menuoperation.errorModal.message" />
                                <br />
                                <Text text="menuoperation.errorModal.checklist.intro" />
                                <ChecklistContainer>
                                    <li>
                                        <Text text="menuoperation.errorModal.checklist.requiredMenuImage" />
                                    </li>
                                    <li>
                                        <Text text="menuoperation.errorModal.checklist.noSubCategories" />
                                    </li>
                                    <li>
                                        <Text text="menuoperation.errorModal.checklist.requiredFields" />
                                    </li>
                                    <li>
                                        <Text text="menuoperation.errorModal.checklist.imageFormats" />
                                    </li>
                                    <li>
                                        <Text text="menuoperation.errorModal.checklist.stepDepthLimit" />
                                    </li>
                                    <li>
                                        <Text text="menuoperation.errorModal.checklist.syncSettings" />
                                    </li>
                                </ChecklistContainer>
                            </div>
                        </Modal>
                        <Modal
                            isOpen={!!downloadError}
                            onHide={clearDownloadError}
                            title="menuoperation.download.error.title"
                            width={500}
                        >
                            <Text text={downloadError || ''} />
                        </Modal>
                    </HeaderContainer>
                </Container>
            </RouterNavigationTabs>
        </>
    );
};

export default MenuOperationListPage;
